export const CRM_SCOPED_VIEW_MODES = [
  'my',
  'team',
  'collab',
  'pool',
  'all',
] as const;

export type CrmScopedViewMode = (typeof CRM_SCOPED_VIEW_MODES)[number];

type CrmViewModule =
  | 'customers'
  | 'contacts'
  | 'opportunities'
  | 'quotes'
  | 'contracts'
  | 'leads';

type ResolveCrmViewModeOptions = {
  allowPool?: boolean;
  defaultViewMode?: Exclude<CrmScopedViewMode, 'pool'> | 'pool';
};

type CrmPageScope = 'self' | 'team' | 'all';

export const isCrmScopedViewMode = (
  value: string | null | undefined,
): value is CrmScopedViewMode =>
  typeof value === 'string' &&
  (CRM_SCOPED_VIEW_MODES as readonly string[]).includes(value);

export const resolveCrmScopedViewModeFromPathname = (
  pathname: string | null | undefined,
  moduleName: CrmViewModule,
  options?: ResolveCrmViewModeOptions,
): CrmScopedViewMode => {
  const normalized = String(pathname || '').trim();
  const defaultViewMode = options?.defaultViewMode || 'my';
  const resourcePath = {
    customers: 'customer',
    contacts: 'contact',
    opportunities: 'opportunity',
    quotes: 'quote',
    contracts: 'contract',
    leads: 'lead',
  }[moduleName];
  const modulePath = `/${resourcePath}/`;
  const [path, query] = normalized.split('?');
  if (path.replace(/\/$/, '') === `${modulePath}overview` && query) {
    const selected = new URLSearchParams(query).get('viewMode');
    if (
      isCrmScopedViewMode(selected) &&
      (selected !== 'pool' || options?.allowPool)
    )
      return selected;
  }

  if (options?.allowPool && normalized.includes(`${modulePath}pool`)) {
    return 'pool';
  }
  if (normalized.includes(`${modulePath}collab`)) {
    return 'collab';
  }
  if (normalized.includes(`${modulePath}team`)) {
    return 'team';
  }
  if (normalized.includes(`${modulePath}all`)) {
    return 'all';
  }
  return defaultViewMode;
};

export const buildScopedViewModeQuery = (
  viewMode: CrmScopedViewMode,
): { viewMode: CrmScopedViewMode } => ({
  viewMode,
});

export const resolveCrmScopedViewModeFromScope = (
  scope?: CrmPageScope | null,
): Exclude<CrmScopedViewMode, 'pool' | 'collab'> =>
  scope === 'team' ? 'team' : scope === 'all' ? 'all' : 'my';
